import { CompositeChart, type CompositeChartSeries } from '@mantine/charts';
import dayjs from 'dayjs';

import { resolveSeriesColor } from '#core/lib/formatters/colors.ts';
import { humanizeKey } from '#core/lib/formatters/humanize-key.ts';
import { formatNumber, inferNumberFormat } from '#core/lib/formatters/number-decimals.ts';

import classes from '../Chart.module.css';
import { STROKE_DASHARRAYS } from '../constants.ts';
import { ChartSeriesSchema } from '../schema.ts';
import type { ChartRendererProps } from '../types.ts';

/**
 * Renders cartesian charts (bar, line, area, composite) using CompositeChart.
 */
export function CartesianChartRenderer({ config, width }: ChartRendererProps) {
  const chartType = getSeriesType(config.chartType);
  const yAxisValues = Object.fromEntries(
    Object.entries(config.data.at(0) ?? {}).filter(([key, value]) => key !== config.xAxis && typeof value === 'number'),
  );

  const seriesConfig = config.series?.length
    ? config.series
    : Object.keys(yAxisValues).map((key) => ChartSeriesSchema.parse({ name: key }));

  const chartSeries: CompositeChartSeries[] = seriesConfig.map((s, i) => ({
    name: s.name,
    // Default legend/tooltip label: humanize the column key. Explicit `s.label`
    // (e.g. domain-specific name supplied by the LLM) always wins.
    label: s.label ?? humanizeKey(s.name),
    strokeDasharray: STROKE_DASHARRAYS[s.lineStyle],
    color: resolveSeriesColor(s, i),
    type: s.type ?? chartType,
  }));

  const isCompact = width < 250;

  // Combine all series values to infer one set of format hints — Mantine's
  // valueFormatter applies to every Y tick and tooltip entry.
  const seriesNames = chartSeries.map((s) => s.name);
  const combinedValues = config.data.flatMap((row) =>
    seriesNames.map((name) => row[name]).filter((v): v is number => typeof v === 'number'),
  );
  const valueHints = inferNumberFormat(combinedValues);
  const valueFormatter = (value: number) => formatNumber(value, valueHints);
  const data = config.data.map((row) => {
    const xValue = row[config.xAxis];
    const xValueIsDate = xValue instanceof Date || (typeof xValue === 'string' && !Number.isNaN(Date.parse(xValue)));
    return {
      ...row,
      [config.xAxis]: xValueIsDate ? dayjs(xValue).valueOf() : xValue,
    };
  });

  return (
    <CompositeChart
      activeDotProps={{
        r: 4,
      }}
      className={classes.chart}
      // connectNulls={false}
      curveType={config.curveType ?? 'linear'}
      data={data}
      dataKey={config.xAxis}
      dotProps={{
        r: 2,
        cursor: 'default',
      }}
      legendProps={{ verticalAlign: 'top' }}
      lineProps={{
        cursor: 'default',
      }}
      maxBarWidth={30}
      referenceLines={config.referenceLines}
      series={chartSeries}
      strokeWidth={1.5}
      tickLine="y"
      title={config.title}
      tooltipProps={{
        labelFormatter: (label) => (typeof label === 'string' ? label : dayjs(label).format('YYYY-MM-DD')),
      }}
      valueFormatter={valueFormatter}
      withLegend={!isCompact}
      xAxisProps={{
        type: data.every((row) => {
          const value = row[config.xAxis];
          return typeof value === 'number' || value instanceof Date;
        })
          ? 'number'
          : 'category',
        tickFormatter(value: number | string) {
          if (typeof value !== 'string') {
            return dayjs(value).format('YYYY-MM-DD');
          }
          return value.toString();
        },
        niceTicks: 'none',
        minTickGap: 10,
        domain: ['auto', 'auto'],
        width: 'auto',
        height: 20,
        fontSize: 12,
        fontWeight: 600,
        tick: {
          fill: 'var(--mantine-color-dark-0)',
        },
      }}
      yAxisProps={{
        domain: ['auto', 'auto'],
        fontSize: 12,
        fontWeight: 600,
        tick: {
          fill: 'var(--mantine-color-dark-0)',
        },
      }}
    />
  );
}

// FIXME: use SSOT type
type SeriesType = 'bar' | 'line' | 'area';

const getSeriesType = (chartType: string): SeriesType => {
  if (chartType === 'bar') {
    return 'bar';
  }
  if (chartType === 'area') {
    return 'area';
  }
  return 'line';
};
